import { api } from "../api/client.ts";
import { getOrCreateDeviceId, setToken } from "../auth.ts";

export interface LoginPageOptions {
  onSuccess: () => void;
}

export class LoginPage {
  private overlay: HTMLElement;

  constructor(opts: LoginPageOptions) {
    this.overlay = this._build(opts);
    document.body.appendChild(this.overlay);
  }

  private _build(opts: LoginPageOptions): HTMLElement {
    const overlay = document.createElement("div");
    overlay.id = "login-overlay";
    overlay.style.cssText = `
      position: fixed; inset: 0; z-index: 500;
      background: var(--bg);
      display: flex; align-items: center; justify-content: center;
    `;

    const card = document.createElement("div");
    card.style.cssText = `
      background: var(--surface);
      border: 1px solid var(--border);
      border-radius: 12px;
      padding: 36px 40px;
      width: 360px;
      max-width: 96vw;
      display: flex;
      flex-direction: column;
      gap: 20px;
    `;

    const heading = document.createElement("h2");
    heading.textContent = "Sign in";
    heading.style.cssText = "font-size:20px;font-weight:700;margin:0";

    const usernameField = this._field("Username", "text", "username");
    const passwordField = this._field("Password", "password", "current-password");

    const usernameInput = usernameField.querySelector("input")!;
    const passwordInput = passwordField.querySelector("input")!;

    const errorEl = document.createElement("div");
    errorEl.style.cssText = `
      color: var(--red); font-size: 13px; min-height: 18px; display: none;
    `;

    const submitBtn = document.createElement("button");
    submitBtn.className = "btn primary";
    submitBtn.textContent = "Sign in";
    submitBtn.style.cssText = "width:100%;justify-content:center;padding:10px";

    const submit = async () => {
      const username = usernameInput.value.trim();
      const password = passwordInput.value;
      if (!username || !password) {
        this._setError(errorEl, "Username and password are required.");
        return;
      }

      submitBtn.disabled = true;
      submitBtn.textContent = "Signing in…";
      this._setError(errorEl, "");

      try {
        const resp = await api.auth.login(username, password, getOrCreateDeviceId());
        setToken(resp.token);
        this.destroy();
        opts.onSuccess();
      } catch (err) {
        this._setError(errorEl, (err as Error).message || "Login failed.");
        submitBtn.disabled = false;
        submitBtn.textContent = "Sign in";
        passwordInput.focus();
      }
    };

    submitBtn.addEventListener("click", submit);
    [usernameInput, passwordInput].forEach((inp) => {
      inp.addEventListener("keydown", (e) => { if (e.key === "Enter") submit(); });
    });

    card.append(heading, usernameField, passwordField, errorEl, submitBtn);
    overlay.appendChild(card);

    // Focus username on next tick (after DOM insertion)
    setTimeout(() => usernameInput.focus(), 0);

    return overlay;
  }

  private _field(label: string, type: string, autocomplete: string): HTMLElement {
    const wrap = document.createElement("div");
    wrap.style.cssText = "display:flex;flex-direction:column;gap:6px";

    const lbl = document.createElement("label");
    lbl.textContent = label;
    lbl.style.cssText = "font-size:13px;color:var(--text-muted)";

    const inp = document.createElement("input");
    inp.type = type;
    inp.setAttribute("autocomplete", autocomplete);
    inp.style.cssText = `
      padding: 8px 12px;
      background: var(--bg);
      border: 1px solid var(--border);
      border-radius: var(--radius);
      color: var(--text);
      font-size: 14px;
      outline: none;
      transition: border-color 120ms;
    `;
    inp.addEventListener("focus", () => (inp.style.borderColor = "var(--accent)"));
    inp.addEventListener("blur", () => (inp.style.borderColor = "var(--border)"));

    wrap.append(lbl, inp);
    return wrap;
  }

  private _setError(el: HTMLElement, msg: string): void {
    el.textContent = msg;
    el.style.display = msg ? "block" : "none";
  }

  destroy(): void {
    this.overlay.remove();
  }
}
